import { useMemo } from 'react';
import { useFinancialData } from '../contexts/FinancialDataContext';
import { useAuth } from '../contexts/AuthContext';
import { TIER_LIMITS, CHART_COLORS } from '../config/constants';
import { calculateReturn, calculateUtilization, calculateAssetDistribution, getBudgetColor, getGoalColor, isGoalOnTrack } from '../utils/calculations';
import { Investment, CryptoHolding, BankAccount, Asset, HSA } from '../types';

interface ChartItem {
  name: string;
  value: number;
  type: 'banking' | 'investment' | 'crypto' | 'asset' | 'retirement' | 'privateEquity' | 'hsa';
  color: string;
  percentage: number;
  startPercentage: number;
}

/**
 * Hook for financial calculations and derived data
 */
export const useFinancialCalculations = () => {
  const {
    bankingData,
    creditCards,
    investments,
    cryptoHoldings,
    retirement401k,
    privateEquityHoldings,
    hsaAccounts,
    assets,
    budgetCategories,
    goals,
    totalBanking,
    totalCreditUsed,
    totalInvestmentValue,
    totalCrypto,
    total401k,
    totalPrivateEquity,
    totalHSA,
    totalAssets,
    totalLiabilities,
    netWorth,
    totalBudgeted,
    totalSpent,
    totalGoalsSaved,
    totalGoalsTarget,
    getVisibleBankingData,
    getVisibleInvestments,
    getVisible401kHoldings,
    getVisibleCrypto,
    getVisiblePrivateEquity,
  } = useFinancialData();
  const { paymentTier } = useAuth();

  // Tier limits
  const tierLimits = TIER_LIMITS[paymentTier];
  const visibleBankingData = getVisibleBankingData(paymentTier);
  const visibleInvestments = getVisibleInvestments(paymentTier);
  const visible401kHoldings = getVisible401kHoldings(paymentTier);
  const visibleCrypto = getVisibleCrypto(paymentTier);
  const visiblePrivateEquity = getVisiblePrivateEquity(paymentTier);

  // Check if at limits
  const isAtBankingLimit = paymentTier === 'free' && bankingData.length >= tierLimits.bankAccounts;
  const isAtInvestmentLimit = paymentTier === 'free' && investments.length >= tierLimits.investments;
  const isAt401kLimit = paymentTier === 'free' && retirement401k.length >= tierLimits.retirement401kHoldings;
  const isAtCryptoLimit = paymentTier === 'free' && cryptoHoldings.length >= tierLimits.cryptoHoldings;
  const isAtPrivateEquityLimit = paymentTier === 'free' && privateEquityHoldings.length >= tierLimits.privateEquityHoldings;
  const hasHiddenBanking = paymentTier === 'free' && bankingData.length > tierLimits.bankAccounts;
  const hasHiddenInvestments = paymentTier === 'free' && investments.length > tierLimits.investments;
  const hasHidden401k = paymentTier === 'free' && retirement401k.length > tierLimits.retirement401kHoldings;
  const hasHiddenCrypto = paymentTier === 'free' && cryptoHoldings.length > tierLimits.cryptoHoldings;
  const hasHiddenPrivateEquity = paymentTier === 'free' && privateEquityHoldings.length > tierLimits.privateEquityHoldings;
  const hiddenBankingCount = hasHiddenBanking ? bankingData.length - tierLimits.bankAccounts : 0;
  const hiddenInvestmentCount = hasHiddenInvestments ? investments.length - tierLimits.investments : 0;
  const hidden401kCount = hasHidden401k ? retirement401k.length - tierLimits.retirement401kHoldings : 0;
  const hiddenCryptoCount = hasHiddenCrypto ? cryptoHoldings.length - tierLimits.cryptoHoldings : 0;
  const hiddenPrivateEquityCount = hasHiddenPrivateEquity ? privateEquityHoldings.length - tierLimits.privateEquityHoldings : 0;

  // Investment returns
  const investmentReturns = useMemo(() => {
    return visibleInvestments.map((inv: Investment) => ({
      ...inv,
      return: calculateReturn(inv.shares, inv.costBasis, inv.currentPrice),
    }));
  }, [visibleInvestments]);

  // Credit card utilization
  const creditCardUtilization = useMemo(() => {
    return creditCards.map(card => ({
      ...card,
      utilization: calculateUtilization(card.balance, card.limit),
    }));
  }, [creditCards]);

  // Asset distribution for charts
  const assetDistribution = useMemo(() => {
    return calculateAssetDistribution(totalAssets, totalBanking, totalInvestmentValue, total401k, totalCrypto, totalPrivateEquity);
  }, [totalAssets, totalBanking, totalInvestmentValue, total401k, totalCrypto, totalPrivateEquity]);

  // Chart data for allocation pie chart
  const chartData = useMemo((): ChartItem[] => {
    const allItems: Omit<ChartItem, 'color' | 'percentage' | 'startPercentage'>[] = [
      ...visibleBankingData
        .filter((a: BankAccount) => a.balance > 0)
        .map((a: BankAccount) => ({
          name: a.name,
          value: a.balance,
          type: 'banking' as const,
        })),
      ...visibleInvestments
        .filter((i: Investment) => i.totalValue > 0)
        .map((i: Investment) => ({
          name: i.symbol,
          value: i.totalValue,
          type: 'investment' as const,
        })),
      ...retirement401k
        .filter((r) => r.balance > 0)
        .map((r) => ({
          name: r.ticker || r.fundName,
          value: r.balance,
          type: 'retirement' as const,
        })),
      ...privateEquityHoldings
        .filter((p) => p.totalValue > 0)
        .map((p) => ({
          name: p.symbol || p.name,
          value: p.totalValue,
          type: 'privateEquity' as const,
        })),
      ...visibleCrypto
        .filter((c: CryptoHolding) => c.totalValue > 0)
        .map((c: CryptoHolding) => ({
          name: c.symbol,
          value: c.totalValue,
          type: 'crypto' as const,
        })),
      ...assets
        .filter((a: Asset) => a.name !== 'Investment Portfolio' && a.value > 0)
        .map((a: Asset) => ({
          name: a.name,
          value: a.value,
          type: 'asset' as const,
        })),
      ...hsaAccounts
        .filter((h: HSA) => h.balance > 0)
        .map((h: HSA) => ({
          name: h.name,
          value: h.balance,
          type: 'hsa' as const,
        })),
    ];

    // Merge items with the same name (e.g. SOL in both investments and crypto)
    const mergedMap = new Map<string, Omit<ChartItem, 'color' | 'percentage' | 'startPercentage'>>();
    for (const item of allItems) {
      const existing = mergedMap.get(item.name);
      if (existing) {
        existing.value += item.value;
      } else {
        mergedMap.set(item.name, { ...item });
      }
    }
    const mergedItems = Array.from(mergedMap.values());

    const itemsTotal = mergedItems.reduce((sum, item) => sum + item.value, 0);

    let cumulativePercentage = 0;
    return mergedItems.map((item, idx) => {
      const percentage = itemsTotal > 0 ? (item.value / itemsTotal) * 100 : 0;
      const startPercentage = cumulativePercentage;
      cumulativePercentage += percentage;

      return {
        ...item,
        color: CHART_COLORS[idx % CHART_COLORS.length],
        percentage,
        startPercentage,
      };
    });
  }, [visibleBankingData, visibleInvestments, visibleCrypto, retirement401k, privateEquityHoldings, assets, hsaAccounts]);

  // Budget utilization
  const budgetUtilization = useMemo(() => {
    return budgetCategories.map(cat => {
      const percentage = cat.budgeted > 0 ? (cat.spent / cat.budgeted) * 100 : 0;
      const remaining = cat.budgeted - cat.spent;
      return {
        ...cat,
        percentage,
        remaining,
        color: getBudgetColor(cat.spent, cat.budgeted),
      };
    });
  }, [budgetCategories]);

  // Goal progress
  const goalProgress = useMemo(() => {
    return goals.map(goal => {
      const percentage = goal.targetAmount > 0 ? (goal.currentAmount / goal.targetAmount) * 100 : 0;
      const remaining = goal.targetAmount - goal.currentAmount;
      const onTrack = isGoalOnTrack(goal);
      return {
        ...goal,
        percentage,
        remaining,
        onTrack,
        color: getGoalColor(goal),
      };
    });
  }, [goals]);

  return {
    // Tier-limited data
    visibleBankingData,
    visibleInvestments,
    visibleCrypto,
    visiblePrivateEquity,
    visible401kHoldings,
    tierLimits,

    // Limit checks
    isAtBankingLimit,
    isAtInvestmentLimit,
    isAt401kLimit,
    isAtCryptoLimit,
    isAtPrivateEquityLimit,
    hasHiddenBanking,
    hasHiddenInvestments,
    hasHidden401k,
    hasHiddenCrypto,
    hasHiddenPrivateEquity,
    hiddenBankingCount,
    hiddenInvestmentCount,
    hidden401kCount,
    hiddenCryptoCount,
    hiddenPrivateEquityCount,

    // Calculations
    investmentReturns,
    creditCardUtilization,
    assetDistribution,
    chartData,
    budgetUtilization,
    goalProgress,

    // Totals
    totalBanking,
    totalCreditUsed,
    totalInvestmentValue,
    totalCrypto,
    total401k,
    totalPrivateEquity,
    totalHSA,
    hsaAccounts,
    totalAssets,
    totalLiabilities,
    netWorth,
    totalBudgeted,
    totalSpent,
    totalGoalsSaved,
    totalGoalsTarget,
  };
};
